import { useState } from "react";
import { MousePointerClick } from "lucide-react";
import girlOrange from "@/assets/optimized-gallery/girl-orange-belt.webp";
import kidsPunch from "@/assets/optimized-gallery/kids-punching-line.webp";
import teenGirl from "@/assets/optimized-gallery/teen-girl-flex.webp";
import kidsFists from "@/assets/optimized-gallery/kids-fists-logo.webp";
import boyGotKicks from "@/assets/optimized-gallery/boy-got-kicks.webp";
import kidsThumbs from "@/assets/optimized-gallery/kids-thumbs-group.webp";
import teensGroup from "@/assets/optimized-gallery/teens-group.webp";
import boyBlue from "@/assets/optimized-gallery/boy-blue-belt.webp";
import boardBreak from "@/assets/optimized-gallery/board-break-action.webp";
import boyWhiteThumbs from "@/assets/optimized-gallery/boy-white-belt-thumbs.webp";
import littleGirlBlack from "@/assets/optimized-gallery/little-girl-black-belt.webp";
import teensSparring from "@/assets/optimized-gallery/teens-sparring-line.webp";
import teenBoyFlex from "@/assets/optimized-gallery/teen-boy-flex.webp";
import teenGirlGreen from "@/assets/optimized-gallery/teen-girl-green-belt.webp";
import girlYellowThumbs from "@/assets/optimized-gallery/girl-yellow-thumbs.webp";
import littleDragonsSparring from "@/assets/optimized-gallery/little-dragons-sparring.webp";
import teamGroup from "@/assets/optimized-gallery/team-group-photo.webp";

export type GalleryPhoto = {
  src: string;
  alt: string;
  title: string;
  blurb: string;
};

export const galleryPhotos: GalleryPhoto[] = [
  {
    src: littleDragonsSparring,
    alt: "Little Dragons sparring with smiles",
    title: "Little Dragons · Ages 4–7",
    blurb:
      "Young students build confidence, focus, coordination, and respect in a fun, safe, and engaging environment.",
  },
  {
    src: boyWhiteThumbs,
    alt: "White belt boy giving two thumbs up",
    title: "Day One Confidence",
    blurb:
      "From the very first class, students gain genuine confidence through positive reinforcement and structured instruction.",
  },
  {
    src: littleGirlBlack,
    alt: "Young girl standing strong in karate gi",
    title: "Strong From the Start",
    blurb:
      "Every lesson teaches discipline, resilience, and emotional control — tools kids use at home, school, and beyond.",
  },
  {
    src: girlYellowThumbs,
    alt: "Yellow belt student with thumbs up",
    title: "Earned, Not Given",
    blurb:
      "Belts are milestones. Character is the real reward — respect, responsibility, and self-belief built one rank at a time.",
  },
  {
    src: kidsFists,
    alt: "Ninja Kids squad fired up with fists raised",
    title: "Ninja Kids · Ages 8–11",
    blurb:
      "Leadership, teamwork, discipline, and self-confidence — built through exciting martial arts challenges.",
  },
  {
    src: kidsPunch,
    alt: "Ninja Kids throwing punches in unison",
    title: "Focus in Motion",
    blurb:
      "Interactive drills and consistent feedback turn energy into focus and effort into real progress.",
  },
  {
    src: kidsThumbs,
    alt: "Side Kicks students smiling with thumbs up",
    title: "Fun That Sticks",
    blurb:
      "Kids enjoy it. Parents trust it. Every class is packed with laughter, learning, and friendships.",
  },
  {
    src: girlOrange,
    alt: "Confident orange belt student flexing",
    title: "Bullyproof Confidence",
    blurb:
      "Self-defense skills paired with mental resilience — so your child stands tall, on and off the mat.",
  },
  {
    src: teensSparring,
    alt: "Teen Leaders class drilling techniques",
    title: "Teen Leaders · 12 & Up",
    blurb:
      "Teens refine advanced techniques, practice real self-defense, and develop leadership skills that carry beyond the dojo.",
  },
  {
    src: teenBoyFlex,
    alt: "Teen boy in black gi flexing with smile",
    title: "Mental Strength",
    blurb:
      "Our Teen Empowerment program builds resilience, confidence, and the grit to navigate peer pressure.",
  },
  {
    src: teenGirlGreen,
    alt: "Teen girl green belt standing strong",
    title: "Leadership in Action",
    blurb:
      "Teens step away from screens and step into self-improvement — physically, mentally, and emotionally stronger.",
  },
  {
    src: teenGirl,
    alt: "Teen leader flexing with a big smile",
    title: "Unstoppable Energy",
    blurb:
      "Dynamic, enjoyable classes motivate teens to embrace a healthier, more active lifestyle they actually love.",
  },
  {
    src: teensGroup,
    alt: "Teen Leaders class group photo",
    title: "The Side Kicks Family",
    blurb:
      "Serving Rome, Whitesboro, Utica & nearby communities — a supportive crew where every student belongs.",
  },
  {
    src: teamGroup,
    alt: "Side Kicks team group photo with all belts",
    title: "Every Belt. Every Story.",
    blurb:
      "From white to black — students grow into respectful, responsible, and self-assured young leaders.",
  },
  {
    src: boyBlue,
    alt: "Blue belt student standing tall and proud",
    title: "Earning the Rank",
    blurb:
      "Each belt represents focus, effort, and growth — a real milestone families celebrate together.",
  },
  {
    src: boyGotKicks,
    alt: "Side Kicks student rocking a 'got kicks?' shirt",
    title: "Built for Kids",
    blurb:
      "We're not your typical martial arts school — we help kids understand their values and live them out daily.",
  },
  {
    src: boardBreak,
    alt: "Black belt jump-kick board break at tournament",
    title: "Break Through Limits",
    blurb:
      "Goal-setting in action. Students learn that with discipline and belief, they can break through anything.",
  },
];

type PhotoGalleryProps = {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  photos?: GalleryPhoto[];
  className?: string;
};

export function PhotoGallery({
  eyebrow = "Our Community",
  title = "Real Students. Real Smiles.",
  subtitle = "A look inside Side Kicks Karate — confidence, focus, and a whole lot of fun.",
  photos = galleryPhotos,
  className = "",
}: PhotoGalleryProps) {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  return (
    <section className={`bg-background py-16 ${className}`}>
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-gold">{eyebrow}</p>
          <h2 className="mt-2 font-display text-3xl md:text-4xl">{title}</h2>
          {subtitle && <p className="mt-3 text-muted-foreground">{subtitle}</p>}
          <p className="mt-3 text-sm font-medium text-ember-deep md:hidden">
            Tap any photo to learn more
          </p>
          <p className="mt-3 hidden text-sm font-medium text-ember-deep md:block">
            Hover over a photo to learn more
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {photos.map((p, i) => {
            const isActive = activeIdx === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIdx(isActive ? null : i)}
                onMouseLeave={() => setActiveIdx((cur) => (cur === i ? null : cur))}
                className={`group relative block w-full overflow-hidden rounded-xl bg-muted text-left shadow-lift transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-gold ${
                  i % 5 === 0 ? "row-span-2 aspect-[3/4] sm:col-span-1" : "aspect-square"
                }`}
              >
                {/* Pulsing "clickable" ring / dot */}
                <span
                  aria-hidden="true"
                   className="pointer-events-none absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink shadow-lg backdrop-blur-sm transition-transform duration-300 group-hover:scale-110"
                >
                  <MousePointerClick className="h-4 w-4" strokeWidth={2.5} />

                </span>
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                   decoding="async"
                   width={720}
                   height={720}
                  className={`h-full w-full object-cover object-top transition-all duration-700 ease-out group-hover:scale-110 group-hover:rotate-1 ${
                    isActive ? "scale-110 rotate-1" : ""
                  }`}
                />
                {/* Overlay info */}
                <div
                  className={`pointer-events-none absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 transition-all duration-500 ${
                    isActive
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0"
                  }`}
                >
                  <p className="font-display text-sm md:text-base font-bold text-white leading-tight drop-shadow">
                    {p.title}
                  </p>
                  <p className="mt-1 text-[11px] md:text-xs text-white/90 leading-snug line-clamp-4">
                    {p.blurb}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
